import React from "react";
import { Link } from "react-router-dom";
import { BedDouble, Bath, Maximize2, MapPin, Calendar, ArrowRight } from "lucide-react";

const CTA_COPY = {
  nl: {
    matchBadge: (p) => `${p}% Match`,
    bookCta: "Boek Persoonlijke Viewing",
    viewCta: "Bekijk woning",
  },
  en: {
    matchBadge: (p) => `${p}% Match`,
    bookCta: "Book Personal Viewing",
    viewCta: "View property",
  },
  de: {
    matchBadge: (p) => `${p}% Match`,
    bookCta: "Persönliche Besichtigung",
    viewCta: "Objekt ansehen",
  },
  es: {
    matchBadge: (p) => `${p}% Match`,
    bookCta: "Reservar Visita Personal",
    viewCta: "Ver propiedad",
  },
};

function formatPrice(price, currency = "EUR") {
  if (!price) return "—";
  const n = Number(price);
  if (Number.isNaN(n)) return String(price);
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `€${n.toLocaleString("nl-NL")}`;
  }
}

export default function MiaMatchCard({ match, lang = "nl", onClose }) {
  if (!match) return null;
  const c = CTA_COPY[lang] || CTA_COPY.nl;
  const prop = match.property || {};
  const pct = Math.max(0, Math.min(100, Number(match.match_percent) || 0));

  return (
    <div
      className="my-2 border border-gold/40 bg-charcoal overflow-hidden"
      data-testid="mia-match-card"
      data-match-ref={match.reference}
    >
      {/* Photo + match badge */}
      <div className="relative aspect-[16/10] bg-charcoal-surface overflow-hidden">
        {prop.main_image && (
          <img
            src={prop.main_image}
            alt={`${prop.type || ""} ${prop.location || ""}`.trim()}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
        <span
          className="absolute top-3 left-3 px-3 py-1 text-[0.68rem] tracking-[0.22em] uppercase font-medium bg-gold text-charcoal-surface"
          style={{ letterSpacing: "0.18em" }}
          data-testid="mia-match-badge"
        >
          {c.matchBadge(pct)}
        </span>
        <span
          aria-hidden="true"
          className="absolute top-0 left-0 h-[2px] w-12"
          style={{ background: "#7BB3D1" }}
        />
      </div>

      {/* Identity */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-serif text-base text-cream leading-tight truncate">
              {prop.type || "Property"} · {prop.location || ""}
            </p>
            <p className="text-[0.7rem] tracking-[0.22em] uppercase mt-1" style={{ color: "#7BB3D1" }}>
              Ref · {match.reference}
            </p>
          </div>
          <p className="font-serif text-base text-gold whitespace-nowrap">
            {formatPrice(prop.price, prop.currency)}
          </p>
        </div>

        {/* Key icons */}
        <div className="flex items-center gap-4 mt-3 text-cream-muted text-xs">
          {prop.bedrooms != null && (
            <span className="inline-flex items-center gap-1.5">
              <BedDouble size={13} strokeWidth={1.5} className="text-gold" />
              {prop.bedrooms}
            </span>
          )}
          {prop.bathrooms != null && (
            <span className="inline-flex items-center gap-1.5">
              <Bath size={13} strokeWidth={1.5} className="text-gold" />
              {prop.bathrooms}
            </span>
          )}
          {prop.built != null && (
            <span className="inline-flex items-center gap-1.5">
              <Maximize2 size={13} strokeWidth={1.5} className="text-gold" />
              {prop.built} m²
            </span>
          )}
          {prop.location && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={13} strokeWidth={1.5} className="text-gold" />
              <span className="truncate">{prop.location}</span>
            </span>
          )}
        </div>

        {/* Reasoning */}
        {match.reasoning && (
          <p className="text-cream-subtle text-[0.78rem] italic leading-relaxed mt-3 font-light">
            "{match.reasoning}"
          </p>
        )}

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <Link
            to="/diensten#viewing-experience"
            onClick={onClose}
            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-[0.7rem] tracking-[0.22em] uppercase bg-gold text-charcoal-surface hover:bg-gold-hover transition-colors"
            data-testid="mia-match-cta-book"
          >
            <Calendar size={13} strokeWidth={1.5} />
            {c.bookCta}
          </Link>
          {match.reference && (
            <Link
              to={`/aanbod/${encodeURIComponent(match.reference)}`}
              onClick={onClose}
              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-[0.7rem] tracking-[0.22em] uppercase border border-gold text-gold hover:bg-gold/10 transition-colors"
              data-testid="mia-match-cta-view"
            >
              {c.viewCta}
              <ArrowRight size={12} strokeWidth={1.5} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
